import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreVertical, Edit, Trash2, Eye, Heart, Shield, Activity, Brain } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FamilyMemberCard = ({ member, onEdit, onDelete, onViewDetails }) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    if (!confirm(t('familyMember.confirmRemove', { name: member.name }))) return;

    setLoading(true);
    try {
      // Auth via httpOnly cookie — no localStorage token needed.
      await axios.delete(`${API}/family-health/members/${member.id}`, {
        withCredentials: true,
      });
      toast.success(t('familyMember.removed', { name: member.name }));
      onDelete(member.id);
    } catch (error) {
      console.error('Error deleting member:', error);
      toast.error(t('familyMember.removeFailed'));
    } finally {
      setLoading(false);
    }
  };

  const getLifeStageColor = (lifeStage) => {
    const colors = {
      infant: 'bg-pink-100 text-pink-700',
      toddler: 'bg-purple-100 text-purple-700',
      child: 'bg-blue-100 text-blue-700',
      adolescent: 'bg-indigo-100 text-indigo-700',
      adult: 'bg-green-100 text-green-700',
      senior: 'bg-amber-100 text-amber-700'
    };
    return colors[lifeStage] || 'bg-gray-100 text-gray-700';
  };

  const getLifeStageLabel = (lifeStage) =>
    t(`familyMember.lifeStage.${lifeStage}`, t('familyMember.lifeStage.unknown'));

  const getInsightIcon = (type) => {
    const icons = {
      immunization: Shield,
      nutrition: Heart,
      activity: Activity,
      preventive: Shield,
      womens_health: Heart,
      mens_health: Activity,
      default: Brain
    };
    const Icon = icons[type] || icons.default;
    return <Icon className="h-4 w-4" />;
  };

  return (
    <Card className="relative hover:shadow-lg transition-shadow duration-200">
      {/* Dropdown Menu */}
      <div className="absolute top-4 right-4 z-10">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onViewDetails(member)}>
              <Eye className="h-4 w-4 mr-2" />
              {t('familyMember.viewDetails')}
            </DropdownMenuItem>
            {onEdit && (
              <DropdownMenuItem onClick={() => onEdit(member)}>
                <Edit className="h-4 w-4 mr-2" />
                {t('familyMember.edit')}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={handleDelete} className="text-red-600" disabled={loading}>
              <Trash2 className="h-4 w-4 mr-2" />
              {t('familyMember.remove')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <CardHeader className="pb-3">
        <div className="flex items-start gap-3">
          {/* Avatar */}
          <div className="text-4xl">{member.avatar_emoji || '👤'}</div>

          <div className="flex-1 min-w-0">
            <CardTitle className="text-xl mb-1 truncate">{member.name}</CardTitle>
            <div className="flex flex-wrap gap-2">
              {member.age && (
                <Badge variant="outline" className="text-xs">
                  {member.age} {t('familyMember.yearsSuffix')}
                </Badge>
              )}
              {member.life_stage && (
                <Badge className={`text-xs ${getLifeStageColor(member.life_stage)}`}>
                  {getLifeStageLabel(member.life_stage)}
                </Badge>
              )}
              {member.relationship && (
                <Badge variant="secondary" className="text-xs capitalize">
                  {member.relationship}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Health Goals */}
        {member.health_goals && member.health_goals.length > 0 && (
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1.5">{t('familyMember.healthGoalsLabel')}</p>
            <div className="flex flex-wrap gap-1">
              {member.health_goals.slice(0, 3).map((goal, idx) => (
                <Badge key={idx} variant="outline" className="text-xs">
                  {goal}
                </Badge>
              ))}
              {member.health_goals.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{member.health_goals.length - 3} {t('familyMember.moreSuffix')}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Insights Preview */}
        {member.insights && member.insights.length > 0 && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 border border-blue-100">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-blue-900">{t('familyMember.activeInsights')}</p>
              <Badge variant="secondary" className="text-xs">
                {member.insight_count || member.insights.length}
              </Badge>
            </div>
            <div className="space-y-1.5">
              {member.insights.slice(0, 2).map((insight, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <div className="text-blue-600 mt-0.5">
                    {getInsightIcon(insight.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-900 truncate">{insight.title}</p>
                    <p className="text-xs text-gray-600 truncate">{insight.message}</p>
                  </div>
                </div>
              ))}
              {member.insights.length > 2 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full h-7 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-100"
                  onClick={() => onViewDetails(member)}
                >
                  {t('familyMember.viewAllInsights', { count: member.insights.length })}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* View Full Profile Button */}
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => onViewDetails(member)}
        >
          {t('familyMember.viewFullProfile')}
        </Button>
      </CardContent>
    </Card>
  );
};

export default FamilyMemberCard;
